import React, { FormEvent, useEffect, useRef, useState } from "react";
import { MessageBarType } from "@fluentui/react";
import { Divider, Field, InfoLabel, Spinner, Switch, Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, Button, Dropdown, Option, Input, Label } from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { connect } from "react-redux";
import { useTranslation, Trans } from "react-i18next";


// functions 
import * as ComponentFunctions from './PractitionerProtocolConceptForm.Functions';

// components
import ContractConceptSelectorComponent from "../../../../commons/component/Inputs/ContractConceptSelector/ContractConceptSelector.Component";

// models
import { ContractConcept } from "../../../../models/Entities/Contracts/ContractConcept";
import { IValueOriginOption } from "../../../Contracts/Concept/ConceptForm/ConceptForm.Functions";
import { CurrencySelectorComponent } from "../../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent";
import MedicalServiceSelectorComponent from "../../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelectorComponent";
import { AvailableMedicalService } from "../../../../models/Entities/MedicalServices/AvailableMedicalService";
import { NewContractCommissionAgentConcept } from "../../../../models/Entities/Contracts/NewContractCommissionAgentConcept";
import { ContractCommissionAgentConcept } from "../../../../models/Entities/Contracts/ContractCommissionAgentConcept";
import { ErrorMessageComponent } from "../../../Error/ErrorMessageComponent";
import { MessageComponent } from "../../../../commons/component/MessageComponent";
import { use } from "i18next";
import { InfoFilled } from "@fluentui/react-icons";


interface FormValidation {
    success: boolean;
    errors: string[];
}


const PractitionerProtocolConceptForm = (props: ComponentFunctions.Props) => {

    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<
        FormValidation | undefined
    >();

    const containerRef = useRef<HTMLDivElement>(null);

    const scrollToTop = () => {
        if (containerRef.current) {
            containerRef.current.scrollTo({
                top: 0,
                behavior: 'smooth', // Optional: Adds a smooth scrolling effect
            });
        }
    };

    const { t } = useTranslation();

    // values
    const [value, setValue] = useState<number>(0);
    const [remoteValue, setRemoteValue] = useState<number>(0);
    const [additionalValue, setAdditionalValue] = useState<number>(0);
    const [failValue, setFailValue] = useState<number>(0);


    // form options
    const [conceptsSelected, setConceptsSelected] = useState<ContractConcept[]>([]);
    const [conceptSelected, setConceptSelected] = useState<ContractConcept>();
    const [valueOriginOptionSelected, setValueOriginOptionSelected] = useState<IValueOriginOption>();
    const [currencySelected, setCurrencySelected] = useState<string>();
    const [medicalServiceSelected, setMedicalServiceSelected] = useState<AvailableMedicalService | undefined>();
    const [takeOnlyItWasDoneByHimself, setTakeOnlyItWasDoneByHimself] = useState<boolean>(false);
    const [takeOnAlreadyRealized, setTakeOnAlreadyRealized] = useState<boolean>(true);
    const [takeOnAlreadyPaid, setTakeOnAlreadyPaid] = useState<boolean>(false);
    const [discountAlreadyPaidToSI, setDiscountAlreadyPaidToSI] = useState<boolean>(false);
    const [payByVisit, setPayByVisit] = useState<boolean>(false);

    // form otions events
    const HandleTakeOnlyItWasDoneByHimself = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setTakeOnlyItWasDoneByHimself(data.checked);
    }

    const HandleTakeOnAlreadyRealized = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setTakeOnAlreadyRealized(data.checked || false);
        setTakeOnAlreadyPaid(!data.checked || false);
    }

    const HandleTakeOnAlreadyPaid = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setTakeOnAlreadyRealized(!data.checked || false);
        setTakeOnAlreadyPaid(data.checked || false);
    }

    const HandleDiscountAlreadyPaidToSI = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setDiscountAlreadyPaidToSI(data.checked || false);
    }

    const HandlePayByVisit = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setPayByVisit(data.checked || false);
    }

    useEffect(() => {
        setValue(props.CommissionAgentConcept?.value || 0);
        setRemoteValue(props.CommissionAgentConcept?.remoteValue || 0);
        setAdditionalValue(props.CommissionAgentConcept?.additionalValue || 0);
        setFailValue(props.CommissionAgentConcept?.failValue || 0);
        //setConceptsSelected([]);
        //setConceptSelected(props.CommissionAgentConcept?.conceptId ? props.CommissionAgentConcept : undefined);
        setValueOriginOptionSelected(ComponentFunctions.GetValueOriginOptionByKey(props.CommissionAgentConcept?.valuationType || 0));
        setCurrencySelected(props.CommissionAgentConcept?.currencyCode || props.Contract?.contractCurrency);
        setMedicalServiceSelected(props.MedicalService);
        setTakeOnlyItWasDoneByHimself(props.CommissionAgentConcept?.takeOnlyItWasDoneByHimself || false);
        setTakeOnAlreadyRealized(true);
        setTakeOnAlreadyPaid(false);
        setDiscountAlreadyPaidToSI(false);
        setPayByVisit(props.CommissionAgentConcept?.payByVisit || false);
    }, [props.CommissionAgentConcept, props.isOpen]);

    useEffect(() => {
        if (submittedForm && props.SavingSuccessfully) {
            props.dismissPanel();
            resetForm();
        }
    }, [submittedForm, props.SavingSuccessfully]);

    const ValidateForm = (): boolean => {
        let validationResult: boolean = true;

        let FormValidation: FormValidation = { success: true, errors: [] };
        setFormValidationStatus(FormValidation);

        if (!payByVisit && !conceptSelected && conceptsSelected.length === 0) {
            FormValidation.errors.push(t('PractitionerProtocolConceptForm.selectConcept'));
            validationResult = false;
        }

        if (!valueOriginOptionSelected) {
            FormValidation.errors.push(t('PractitionerProtocolConceptForm.selectValorizationType'));
            validationResult = false;
        }

        if (valueOriginOptionSelected?.isFixedPrice && !currencySelected) {
            FormValidation.errors.push(t('PractitionerProtocolConceptForm.selectCurrency'));
            validationResult = false;
        }

        if (!valueOriginOptionSelected?.isPriceList && !valueOriginOptionSelected?.isPercentage) {
            if (value === null || value === undefined) {
                FormValidation.errors.push(t('PractitionerProtocolConceptForm.enterValue'));
                validationResult = false;
            }
            if (remoteValue === null || remoteValue === undefined) {
                FormValidation.errors.push(t('PractitionerProtocolConceptForm.enterRemoteValue'));
                validationResult = false;
            }
            if (additionalValue === null || additionalValue === undefined) {
                FormValidation.errors.push(t('PractitionerProtocolConceptForm.enterAdditionalValue'));
                validationResult = false;
            }
            if (failValue === null || failValue === undefined) {
                FormValidation.errors.push(t('PractitionerProtocolConceptForm.enterFailValue'));
                validationResult = false;
            }
        }

        if (!medicalServiceSelected && valueOriginOptionSelected?.isPriceList) {
            FormValidation.errors.push(t('PractitionerProtocolConceptForm.selectMedicalService'));
            validationResult = false;
        }

        if (valueOriginOptionSelected?.isPercentage && (value) > 100) {
            FormValidation.errors.push(t('PractitionerProtocolConceptForm.percentageLimit'));
            validationResult = false;
        }

        if (!validationResult) {
            scrollToTop();
        }

        FormValidation.success = validationResult;
        setFormValidationStatus(FormValidation);
        return validationResult;
    };

    const resetForm = () => {
        setValue(0);
        setRemoteValue(0);
        setAdditionalValue(0);
        setFailValue(0);
        setConceptsSelected([]);
        setConceptSelected(undefined);
        setValueOriginOptionSelected(ComponentFunctions.ValueOriginOptions[0]);
        setCurrencySelected(props.Contract?.contractCurrency || '');
        setMedicalServiceSelected(undefined);
        setTakeOnlyItWasDoneByHimself(false);
        setTakeOnAlreadyRealized(true);
        setTakeOnAlreadyPaid(false);
        setDiscountAlreadyPaidToSI(false);
        setPayByVisit(false);
        setSubmittedForm(false);
        setFormValidationStatus(undefined);
    }

    // events
    const HandleCancelForm = () => {
        props.dismissPanel();
    }

    // events
    const HandleSaveForm = () => {
        setSubmittedForm(true);
        if (ValidateForm()) {
            let currentCommissionAgentConcept: NewContractCommissionAgentConcept | ContractCommissionAgentConcept | undefined;
            if (props.CommissionAgentConceptId) {
                // For edit - conceptId should be undefined when payByVisit is true
                const editConcept: ContractCommissionAgentConcept = {
                    id: props.CommissionAgentConceptId,
                    commissionAgentId: props.CommissionAgentId!,
                    contractId: props.ContractId,
                    valuationType: Number(valueOriginOptionSelected?.key) || 0,
                    currencyCode: currencySelected!,
                    value: value,
                    remoteValue: remoteValue,
                    additionalValue: additionalValue,
                    failValue: failValue,
                    medicalServiceId: medicalServiceSelected?.id || 0,
                    takeOnlyItWasDoneByHimself: takeOnlyItWasDoneByHimself || false,
                    takeOnAlreadyRealized: takeOnAlreadyRealized || false,
                    takeOnAlreadyPaid: takeOnAlreadyPaid || false,
                    discountAlreadyPaidToSI: discountAlreadyPaidToSI || false,
                    payByVisit: payByVisit || false,
                    conceptId: conceptSelected ? conceptSelected.id : undefined
                };

                // Only include conceptId if payByVisit is false and a concept is selected
                if (!payByVisit && conceptSelected?.id) {
                    editConcept.conceptId = conceptSelected.id;
                }

                props.UpdateContractCommissionAgentConcept(editConcept as ContractCommissionAgentConcept);
            }
            else {
                // For create - conceptId should be undefined when payByVisit is true
                const createConcept: NewContractCommissionAgentConcept = {
                    commissionAgentId: props.CommissionAgentId!,
                    contractId: props.ContractId,
                    valuationType: Number(valueOriginOptionSelected?.key) || 0,
                    currencyCode: currencySelected!,
                    value: value,
                    remoteValue: remoteValue,
                    additionalValue: additionalValue,
                    failValue: failValue,
                    medicalServiceId: medicalServiceSelected?.id || 0,
                    takeOnlyItWasDoneByHimself: takeOnlyItWasDoneByHimself || false,
                    takeOnAlreadyRealized: takeOnAlreadyRealized || false,
                    takeOnAlreadyPaid: takeOnAlreadyPaid || false,
                    discountAlreadyPaidToSI: discountAlreadyPaidToSI || false,
                    payByVisit: payByVisit || false,
                    conceptId: conceptsSelected.length > 0 && !payByVisit ? conceptsSelected.map((concept: ContractConcept) => concept.id ) : undefined
                };

                props.CreateContractCommissionAgentConcept(props.ContractId, props.CommissionAgentId!, createConcept as NewContractCommissionAgentConcept);
            }
        }
    }

    const FormField = (
        <>
            <Field className='ToggleInline'
                validationState="none"
                validationMessageIcon={<InfoFilled />}
                validationMessage={t('PractitionerProtocolConceptForm.payByVisitInfo')}>
                <Switch
                    label={t('PractitionerProtocolConceptForm.payByVisit')}
                    labelPosition="before"
                    checked={payByVisit}
                    onChange={(ev, data) => HandlePayByVisit(ev, { checked: data.checked })}
                />
            </Field>
            {!payByVisit && (
                <ContractConceptSelectorComponent
                    onSelectedContractConcept={(contractConcept: ContractConcept[]) => { props.CommissionAgentConceptId ? setConceptSelected(contractConcept[0]) : setConceptsSelected(contractConcept) }}
                    ContractConceptsSelected={props.CommissionAgentConcept && props.CommissionAgentConcept.conceptId !== undefined ? [props.CommissionAgentConcept.conceptId] : undefined}
                    MultiselectEnable={props.CommissionAgentConceptId ? false : true}
                    noteText={t('PractitionerProtocolConceptForm.conceptNote')}
                    ContractId={props.ContractId}
                    referralDate={undefined}
                />
            )}
            <Divider />
            <Field label={t('conceptForm.formFields.valorization')}>
                <Dropdown
                    value={valueOriginOptionSelected ? t(valueOriginOptionSelected.text) : ''}
                    selectedOptions={valueOriginOptionSelected ? [valueOriginOptionSelected.key.toString()] : []}
                    onOptionSelect={(event, data) => {
                        const selected = ComponentFunctions.ValueOriginOptions.find(opt => opt.key.toString() === data.optionValue);
                        if (selected) setValueOriginOptionSelected(selected);
                    }}
                >
                    {ComponentFunctions.ValueOriginOptions.map((option: IValueOriginOption) => (
                        <Option key={option.key.toString()} value={option.key.toString()} text={t(option.text) || ''}>
                            {t(option.text)}
                        </Option>
                    ))}
                </Dropdown>
            </Field>
            {valueOriginOptionSelected?.isFixedPrice &&
                <CurrencySelectorComponent
                    defaultSelected={currencySelected || ''}
                    onCurrencyChange={(currency: string) => setCurrencySelected(currency)}
                    errorMessage={undefined}
                    label={t('settlementDetailForm.formData.currency')}
                    isRequired={true}
                />
            }
            {!valueOriginOptionSelected?.isPriceList &&
                <>
                    <Field label={t('conceptForm.formFields.value')}>
                        <Input
                            value={value.toString()}
                            type="number"
                            onFocus={(event) => event.currentTarget.select()}
                            onChange={(event, data) => setValue(Number(data.value) || 0)}
                            contentAfter={valueOriginOptionSelected?.isFixedPrice ? currencySelected : valueOriginOptionSelected?.isPercentage ? '%' : undefined}
                        />
                    </Field>
                    {valueOriginOptionSelected?.isFixedPrice &&
                        <>
                            <Field label={t('PractitionerProtocolConceptForm.remoteValue')}>
                                <Input
                                    value={remoteValue.toString()}
                                    type="number"
                                    onFocus={(event) => event.currentTarget.select()}
                                    onChange={(event, data) => setRemoteValue(Number(data.value) || 0)}
                                    contentAfter={valueOriginOptionSelected?.isFixedPrice ? currencySelected : valueOriginOptionSelected?.isPercentage ? '%' : undefined}
                                />
                            </Field>
                            <Field label={t('PractitionerProtocolConceptForm.additionalValue')}>
                                <Input
                                    value={additionalValue.toString()}
                                    type="number"
                                    onFocus={(event) => event.currentTarget.select()}
                                    onChange={(event, data) => setAdditionalValue(Number(data.value) || 0)}
                                    contentAfter={valueOriginOptionSelected?.isFixedPrice ? currencySelected : valueOriginOptionSelected?.isPercentage ? '%' : undefined}
                                />
                            </Field>
                            <Field label={t('PractitionerProtocolConceptForm.failValue')}>
                                <Input
                                    value={failValue.toString()}
                                    type="number"
                                    onFocus={(event) => event.currentTarget.select()}
                                    onChange={(event, data) => setFailValue(Number(data.value) || 0)}
                                    contentAfter={valueOriginOptionSelected?.isFixedPrice ? currencySelected : valueOriginOptionSelected?.isPercentage ? '%' : undefined}
                                />
                            </Field>
                        </>
                    }

                </>
            }
            {valueOriginOptionSelected?.isPriceList &&
                <InfoLabel
                    info={
                        <>
                            {t('PractitionerProtocolConceptForm.priceListInfo')}
                        </>
                    }
                    {...props}
                >
                    <MedicalServiceSelectorComponent
                        onSelectedServices={(services: AvailableMedicalService[] | undefined) => services ? setMedicalServiceSelected(services[0]) : setMedicalServiceSelected(undefined)}
                        noteText={t('PractitionerProtocolConceptForm.medicalServiceNote')}
                        MedicalServicesSelectedIds={medicalServiceSelected ? [medicalServiceSelected.id] : []}
                        MultiselectEnable={false} />
                </InfoLabel>
            }
            <Divider />
            <Field className='ToggleInline'
                validationState="none"
                validationMessageIcon={<InfoFilled />}
                validationMessage={t('PractitionerProtocolConceptForm.takeOnlyByPractitionerInfo')}>

                <Switch
                    label={t('PractitionerProtocolConceptForm.takeOnlyByPractitioner')}
                    labelPosition="before"
                    checked={takeOnlyItWasDoneByHimself}
                    onChange={(ev, data) => HandleTakeOnlyItWasDoneByHimself(ev, { checked: data.checked })}
                />
            </Field>
            <Field className='ToggleInline'
                validationState="none"
                validationMessageIcon={<InfoFilled />}
                validationMessage={t('PractitionerProtocolConceptForm.settleOnRealizedInfo')}>

                <Switch
                    label={t('PractitionerProtocolConceptForm.settleOnRealized')}
                    labelPosition="before"
                    checked={takeOnAlreadyRealized}
                    onChange={(ev, data) => HandleTakeOnAlreadyRealized(ev, { checked: data.checked })}
                />
            </Field>
            <Field className='ToggleInline'
                validationState="none"
                validationMessageIcon={<InfoFilled />}
                validationMessage={t('PractitionerProtocolConceptForm.settleOnPaidInfo')}>

                <Switch
                    label={t('PractitionerProtocolConceptForm.settleOnPaid')}
                    labelPosition="before"
                    checked={takeOnAlreadyPaid}
                    onChange={(ev, data) => HandleTakeOnAlreadyPaid(ev, { checked: data.checked })}
                />
            </Field>
            <Field className='ToggleInline'
                validationState="none"
                validationMessageIcon={<InfoFilled />}
                validationMessage={t('PractitionerProtocolConceptForm.discountPaidByTeamInfo')}>

                <Switch
                    label={t('PractitionerProtocolConceptForm.discountPaidByTeam')}
                    labelPosition="before"
                    checked={discountAlreadyPaidToSI}
                    onChange={(ev, data) => HandleDiscountAlreadyPaidToSI(ev, { checked: data.checked })}
                />
            </Field>
        </>
    );

    const FormValidationStatusMessage = (FormValidationStatus?.errors?.length ? FormValidationStatus?.errors?.length > 0 : false) && (
        <>
            <MessageComponent
                message={t('PractitionerProtocolConceptForm.validationError')}
                type={MessageBarType.error}
                subMessage={t('PractitionerProtocolConceptForm.validationErrorSub')}
                itemList={FormValidationStatus?.errors || []}
            />
        </>
    );

    const SaveErrorStatusMessage = (props.FailOnSaving && props.ErrorOnSaving) && (
        <>
            <MessageComponent
                message={t('PractitionerProtocolConceptForm.saveError')}
                type={MessageBarType.error}
                subMessage={props.ErrorOnSaving.ErrorMessage}
                itemList={props.ErrorOnSaving?.Errors || []}
            />
        </>
    );

    // footer
    const FooterContent = (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', padding: '8px' }}>
            <Button onClick={HandleCancelForm} appearance="secondary" disabled={props.isSaving} type="button">
                <Trans i18nKey={'conceptForm.footer.cancelButton'} />
            </Button>
            <Button
                onClick={HandleSaveForm}
                disabled={props.isSaving}
                appearance="primary"
                type="button"
                icon={props.isSaving ? <Spinner size="extra-tiny" /> : undefined}
            >
                {props.isSaving ? t('common.saving') : <Trans i18nKey={'conceptForm.footer.saveButton'} />}
            </Button>
        </div>
    );


    return (
        <Drawer
            type="overlay"
            separator
            open={props.isOpen}
            position="end"
            size="medium"
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            aria-label="Close"
                            icon={<Dismiss24Regular />}
                            onClick={HandleCancelForm}
                        />
                    }
                >
                    {t('PractitionerProtocolConceptForm.headerText')}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <div ref={containerRef} style={{ height: '100%', overflowY: 'auto', paddingRight: '16px' }}>
                    {FormValidationStatusMessage}
                    {SaveErrorStatusMessage}
                    {FormField}
                </div>
            </DrawerBody>
            <DrawerFooter>
                {FooterContent}
            </DrawerFooter>
        </Drawer>
    );
}

export default connect(
    ComponentFunctions.mapStateToProps,
    (ComponentFunctions.mapDispatchToProps)
)(PractitionerProtocolConceptForm as any);